/**
 * Screen 5 — the Retention Register.
 *
 * Retention is derived from the Retention deductions actually applied, so the
 * register is their sum and never an independently asserted number. It is sized
 * against its own denominator (certified value), and releases are real events,
 * so the balance is shown falling as well as rising.
 *
 * Grouped by contract within vendor, because that is the unit the release
 * milestone belongs to. Past-milestone balances are surfaced first — a balance
 * held beyond its milestone is money the vendor is entitled to ask for.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { day, int, money, num, stamp, useIptTheme } from './iptTheme';
import { Bar, Btn, Def, Empty, Field, Input, Panel, Sample, Tile } from './IptPrimitives';

export const IptRetention = ({ projectId, isVendor }) => {
  const T = useIptTheme();
  const [d, setD] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});
  const [rel, setRel] = useState(null);
  const [onlyOverdue, setOnlyOverdue] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const sp = new URLSearchParams();
      if (projectId) sp.set('project_id', projectId);
      const [a, b] = await Promise.all([
        api.get(`/ipt/retentions?${sp}`),
        api.get(`/ipt/retentions/events?${sp}`),
      ]);
      setD(a.data);
      setEvents(b.data?.rows || b.data?.events || []);
    } catch { setD(null); } finally { setLoading(false); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const groups = useMemo(() => {
    const rows = (d?.rows || []).filter((r) => !onlyOverdue || r.isPastMilestone);
    const byVendor = {};
    rows.forEach((r) => {
      const v = byVendor[r.vendor_id] || {
        vendor_id: r.vendor_id, vendor_name: r.vendor_name, rows: [],
        withheld: 0, released: 0, balance: 0, overdue: 0,
      };
      v.rows.push(r);
      v.withheld += Number(r.total_withheld || 0);
      v.released += Number(r.released_to_date || 0);
      v.balance += Number(r.balance_held || 0);
      if (r.isPastMilestone) v.overdue += 1;
      byVendor[r.vendor_id] = v;
    });
    return Object.values(byVendor).sort((a, b) => (b.overdue - a.overdue)
      || (b.balance - a.balance));
  }, [d, onlyOverdue]);

  if (loading && !d) {
    return (
      <div className="py-20 text-center" data-testid="ipt-retention-loading">
        <Loader2 className="w-5 h-5 animate-spin mx-auto" style={{ color: T.primary }} />
      </div>
    );
  }
  if (!d) return <Empty T={T} testId="ipt-retention-error">Retention could not be loaded.</Empty>;

  const t = d.totals;
  const m = d.magnitude;
  const cur = d.rows?.[0]?.currency || 'INR';

  if (!(d.rows || []).length) {
    return (
      <Panel T={T} testId="ipt-retention-empty" title="No retention held"
        subtitle="Nothing withheld — an empty register, not a failed load">
        <p className="text-[11.5px] leading-relaxed max-w-[640px]" style={{ color: T.body }}>
          Retention is the sum of the Retention deductions actually applied to this
          project&apos;s invoices. None have been applied yet, so there is nothing to hold
          or release.
        </p>
      </Panel>
    );
  }

  return (
    <div className="space-y-4" data-testid="ipt-retention">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Tile T={T} testId="ret-tile-balance" label="Held now" value={money(t.balance, cur)}
          sub={`${int(t.contracts)} contracts · ${int(t.vendors)} vendors`}
          def={{ title: 'Retention held', body: d.basis }} />
        <Tile T={T} testId="ret-tile-withheld" label="Withheld to date"
          value={money(t.withheld, cur)}
          sub={`${num(m.pctOfCertifiedValue, 2)}% of certified value`}
          def={{ title: 'Withheld', body: m.denominator, sample: m.statement }} />
        <Tile T={T} testId="ret-tile-released" label="Released" value={money(t.released, cur)}
          sub={`${int(t.releaseEvents)} events · ${num(m.releasedPctOfWithheld, 1)}% of withheld`}
          def={{ title: 'Released',
            body: 'Every release is an event, not only a shrinking number on the '
              + 'retention record, so the register has a stream to show.' }} />
        <Tile T={T} testId="ret-tile-overdue" label="Past release milestone"
          value={int(t.pastMilestone)} tone={t.pastMilestone ? T.accent : undefined}
          sub={money(t.pastMilestoneValue, cur)}
          onClick={() => setOnlyOverdue((v) => !v)}
          def={{ title: 'Past release milestone',
            body: 'The release milestone date has passed and a balance is still held. '
              + 'This is the same count the Pending Payment dashboard tile shows — both '
              + 'read one rollup, so they cannot disagree.' }} />
        <Tile T={T} testId="ret-tile-certified" label="Certified value"
          value={money(m.certifiedValue, cur)}
          sub={`${int(m.certifiedInvoices)} invoices — the denominator`}
          def={{ title: 'Certified value', body: m.denominator }} />
        <Tile T={T} testId="ret-tile-pct" label="Retention rate"
          value={`${num(m.pctOfCertifiedValue, 2)}%`}
          sub="withheld ÷ certified value"
          def={{ title: 'Retention rate', body: m.statement }} />
      </div>
      <Sample T={T} testId="ret-sample" text={`${m.statement} ${d.basis}`} />

      {onlyOverdue && (
        <div className="flex items-center gap-2 text-[10.5px]" data-testid="ret-filter-chip">
          <span className="px-2 py-0.5 rounded-full"
            style={{ background: T.tint(T.accent, '1F'), color: T.accent }}>
            past release milestone only
          </span>
          <button type="button" onClick={() => setOnlyOverdue(false)}
            data-testid="ret-clear-filter" style={{ color: T.muted }}>clear</button>
        </div>
      )}

      <Panel T={T} testId="ret-register" title="By contract, within vendor"
        subtitle={`${int(groups.length)} vendors · ${int((d.rows || []).length)} balances`}
        right={<Def T={T} testId="ret-register-def" title="Retention register"
          body={d.basis}
          sample={'Grouped by contract within vendor because the release milestone belongs '
            + 'to the contract, not to the invoice.'} />}>
        <div className="space-y-1">
          {groups.map((g) => {
            const isOpen = open[g.vendor_id] !== false;
            return (
              <div key={g.vendor_id} style={{ borderTop: `1px solid ${T.border}` }}>
                <button type="button" data-testid={`ret-vendor-${g.vendor_id}`}
                  onClick={() => setOpen((o) => ({ ...o, [g.vendor_id]: !isOpen }))}
                  className="w-full flex items-center gap-2 py-2 text-[11.5px] text-left">
                  {isOpen ? <ChevronDown className="w-3 h-3" style={{ color: T.muted }} />
                    : <ChevronRight className="w-3 h-3" style={{ color: T.muted }} />}
                  <span className="font-semibold" style={{ color: T.ink }}>{g.vendor_name}</span>
                  <span className="text-[10px]" style={{ color: T.muted }}>
                    {int(g.rows.length)} contracts
                  </span>
                  {!!g.overdue && (
                    <span className="inline-flex items-center gap-1 px-1.5 rounded text-[9px] font-bold uppercase"
                      data-testid={`ret-vendor-overdue-${g.vendor_id}`}
                      style={{ background: T.tint(T.accent, '1F'), color: T.accent }}>
                      <AlertTriangle className="w-2.5 h-2.5" /> {int(g.overdue)} past milestone
                    </span>
                  )}
                  <span className="ml-auto tabular-nums" style={{ color: T.muted }}>
                    withheld {money(g.withheld, cur)} · released {money(g.released, cur)}
                  </span>
                  <span className="tabular-nums font-semibold w-28 text-right"
                    style={{ color: T.ink }}>{money(g.balance, cur)}</span>
                </button>
                {isOpen && (
                  <table className="w-full text-[11px] mb-2">
                    <thead>
                      <tr style={{ color: T.muted }}>
                        {['Contract', 'Rate', 'Milestone', 'Due', 'Withheld', 'Released',
                          'Balance', ''].map((h, i) => (
                            <th key={h || i}
                              className={`py-1 px-2 font-semibold whitespace-nowrap ${i > 3 ? 'text-right' : 'text-left'}`}>
                              {h}
                            </th>
                          ))}
                      </tr>
                    </thead>
                    <tbody>
                      {g.rows.map((r) => (
                        <tr key={r.retention_id} data-testid={`ret-row-${r.contract_id}`}
                          style={{ borderTop: `1px solid ${T.border}`,
                                   background: r.isPastMilestone
                                     ? T.tint(T.accent, '0E') : 'transparent' }}>
                          <td className="py-1.5 px-2 font-semibold" style={{ color: T.ink }}>
                            {r.cc_contract_id ? (
                              <a href={`/contracts/${r.cc_contract_id}`}
                                data-testid={`ret-cc-link-${r.contract_id}`}
                                className="hover:underline" style={{ color: T.primary }}
                                title="Open in Contracts & Claims">
                                {r.contract_id}
                              </a>
                            ) : r.contract_id}
                            <span className="block text-[9px]" style={{ color: T.muted }}>
                              {r.derived_from}
                            </span>
                          </td>
                          <td className="py-1.5 px-2 tabular-nums" style={{ color: T.body }}>
                            {num(r.retention_pct, 2)}%
                          </td>
                          <td className="py-1.5 px-2" style={{ color: T.body }}>
                            {r.release_milestone}
                          </td>
                          <td className="py-1.5 px-2 tabular-nums" style={{ color: T.body }}>
                            {day(r.release_due_date)}
                            {r.isPastMilestone && (
                              <span className="ml-1 text-[9px] font-bold"
                                data-testid={`ret-overdue-${r.contract_id}`}
                                style={{ color: T.accent }}>
                                +{num(r.daysPastMilestone, 0)} d
                              </span>
                            )}
                          </td>
                          <td className="py-1.5 px-2 text-right tabular-nums"
                            style={{ color: T.body }}>{money(r.total_withheld, r.currency)}</td>
                          <td className="py-1.5 px-2 text-right tabular-nums"
                            style={{ color: T.green }}>
                            {r.released_to_date ? money(r.released_to_date, r.currency) : '—'}
                            {!!r.releaseCount && (
                              <span className="block text-[9px]" style={{ color: T.muted }}>
                                {int(r.releaseCount)} event{r.releaseCount > 1 ? 's' : ''}
                                {r.released_on ? ` · last ${day(r.released_on)}` : ''}
                              </span>
                            )}
                          </td>
                          <td className="py-1.5 px-2 text-right tabular-nums font-semibold"
                            style={{ color: T.ink }}>
                            {money(r.balance_held, r.currency)}
                            <Bar T={T} height={4} segments={[
                              { label: 'released', value: r.released_to_date, colour: T.green },
                              { label: 'held', value: r.balance_held, colour: T.primary },
                            ]} />
                          </td>
                          <td className="py-1.5 px-2 text-right">
                            {!isVendor && Number(r.balance_held || 0) > 0 && (
                              <button type="button" data-testid={`ret-release-${r.contract_id}`}
                                onClick={() => setRel({ row: r, amount: '', doc: '',
                                  milestone: r.release_milestone, remarks: '' })}
                                className="px-2 py-0.5 rounded text-[10px] font-semibold"
                                style={{ background: T.tint(T.primary, '1A'), color: T.primary,
                                         border: `1px solid ${T.tint(T.primary, '44')}` }}>
                                Release
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>
      </Panel>

      <Panel T={T} testId="ret-events" title="Release events"
        subtitle={`${int(events.length)} recorded — the balance falls as well as rises`}
        right={<Def T={T} testId="ret-events-def" title="Release stream"
          body={'Each release is an event with its amount, milestone, document reference '
            + 'and actor. The balance on the retention record is the consequence of these '
            + 'events, not a figure typed over the top of them.'} />}>
        <ol className="space-y-0">
          {events.slice(0, 40).map((e, i) => (
            <li key={e.release_id || i} className="flex gap-3 py-1.5 text-[11px]"
              data-testid={`ret-event-${i}`}
              style={{ borderTop: i ? `1px solid ${T.border}` : 'none' }}>
              <span className="w-[108px] shrink-0 text-[10px] tabular-nums"
                style={{ color: T.muted }}>{stamp(e.released_on)}</span>
              <span className="min-w-0 flex-1" style={{ color: T.body }}>
                <span className="font-semibold" style={{ color: T.ink }}>
                  {e.vendor_name || e.vendor_id}
                </span>
                {' · '}{e.contract_id}
                {e.milestone ? ` · ${e.milestone}` : ''}
                {e.release_doc_ref ? ` · ${e.release_doc_ref}` : ''}
                <span className="text-[9.5px] ml-1.5" style={{ color: T.muted }}>
                  {e.actor_id}
                </span>
              </span>
              <span className="tabular-nums font-semibold" style={{ color: T.green }}>
                {money(e.amount, e.currency || cur)}
              </span>
            </li>
          ))}
          {!events.length && <Empty T={T}>No releases recorded yet.</Empty>}
        </ol>
      </Panel>

      {rel && (
        <ReleaseDialog T={T} state={rel} setState={setRel} onDone={load} />
      )}
    </div>
  );
};

/* ── release: a confirmation with a reason, not a bare click ──────────── */
const ReleaseDialog = ({ T, state, setState, onDone }) => {
  const [busy, setBusy] = useState(false);
  const r = state.row;
  const bal = Number(r.balance_held || 0);
  const amt = state.amount === '' ? bal : Number(state.amount);
  const invalid = !(amt > 0) || amt > bal + 0.01;

  const submit = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/ipt/retentions/${r.retention_id}/release`, {
        amount: amt, release_doc_ref: state.doc || null,
        milestone: state.milestone || null, remarks: state.remarks || null,
      });
      toast.success(`Released ${res.data.amount} · balance now ${res.data.balance_held}`);
      setState(null);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Release refused');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      data-testid="ret-release-dialog" style={{ background: 'rgba(3,7,18,0.6)' }}>
      <div className="w-full max-w-[460px] rounded-lg p-4"
        style={{ background: T.surface, border: `1px solid ${T.borderStrong}` }}>
        <div className="text-[13px] font-semibold" style={{ color: T.ink }}>
          Release retention — {r.contract_id}
        </div>
        <p className="text-[10.5px] mt-1 leading-relaxed" style={{ color: T.body }}>
          {r.vendor_name} · milestone {r.release_milestone} · balance held{' '}
          {money(bal, r.currency)}
          {r.isPastMilestone
            ? ` · ${num(r.daysPastMilestone, 0)} days past the milestone`
            : ' · the milestone date has not yet passed'}
        </p>
        <div className="grid grid-cols-2 gap-2 mt-3">
          <Field T={T} label={`Amount (max ${money(bal, r.currency)})`}>
            <Input T={T} type="number" value={state.amount} data-testid="ret-release-amount"
              placeholder={String(bal)}
              onChange={(e) => setState({ ...state, amount: e.target.value })} />
          </Field>
          <Field T={T} label="Release document ref">
            <Input T={T} value={state.doc} data-testid="ret-release-doc"
              placeholder="RR-00000"
              onChange={(e) => setState({ ...state, doc: e.target.value })} />
          </Field>
          <Field T={T} label="Milestone reached">
            <Input T={T} value={state.milestone} data-testid="ret-release-milestone"
              onChange={(e) => setState({ ...state, milestone: e.target.value })} />
          </Field>
          <Field T={T} label="Remarks">
            <Input T={T} value={state.remarks} data-testid="ret-release-remarks"
              onChange={(e) => setState({ ...state, remarks: e.target.value })} />
          </Field>
        </div>
        {invalid && (
          <p className="text-[10px] mt-2" data-testid="ret-release-invalid"
            style={{ color: T.red }}>
            A release must be positive and cannot exceed the balance held.
          </p>
        )}
        <p className="text-[10px] mt-2" style={{ color: T.muted }}>
          This writes a release event and a Retention Released payment event, and appends to
          the hash-chained audit. The balance falls as a consequence — it is never typed over.
        </p>
        <div className="flex items-center gap-2 mt-3 justify-end">
          <Btn T={T} variant="ghost" onClick={() => setState(null)}
            testId="ret-release-cancel">Cancel</Btn>
          <Btn T={T} onClick={submit} disabled={busy || invalid}
            testId="ret-release-confirm">
            {busy ? 'Releasing…' : 'Confirm release'}
          </Btn>
        </div>
      </div>
    </div>
  );
};

export default IptRetention;
